import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-600 mb-6">你访问的文章不存在或已被移除。</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-label text-sm cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        <article>
          <div className="bg-background-950 text-white">
            <div className="max-w-4xl mx-auto px-4 md:px-6 py-16">
              <Breadcrumb
                items={[
                  { label: categoryMap[article.category] || article.category, href: `/category/${article.category}` },
                  { label: article.title },
                ]}
              />
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs font-label bg-accent-500/20 text-accent-300 px-2.5 py-1 rounded-full hover:bg-accent-500/30 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {categoryMap[article.category] || article.category}
                </Link>
                {article.tags.map((tag) => (
                  <span key={tag} className="text-xs text-background-400 bg-background-800 px-2.5 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-background-400">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-xs" /> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-xs" /> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-xs" /> {article.readTime}
                </span>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
            <div className="mb-10 rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-600 font-label">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-16 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.slug} article={rel} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <style>{`
        .prose-custom h2 { font-family: var(--font-heading); font-size: 1.5rem; font-weight: 700; color: oklch(var(--foreground-900)); margin-top: 2.5rem; margin-bottom: 1rem; }
        .prose-custom h3 { font-family: var(--font-heading); font-size: 1.25rem; font-weight: 600; color: oklch(var(--foreground-900)); margin-top: 2rem; margin-bottom: 0.75rem; }
        .prose-custom p { margin-bottom: 1rem; font-size: 0.95rem; line-height: 1.85; color: oklch(var(--foreground-800)); }
        .prose-custom ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 1rem; }
        .prose-custom li { margin-bottom: 0.5rem; font-size: 0.95rem; line-height: 1.75; color: oklch(var(--foreground-800)); }
        .prose-custom strong { color: oklch(var(--foreground-900)); font-weight: 600; }
      `}</style>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': article.title,
        'description': article.excerpt,
        'author': { '@type': 'Person', 'name': article.author },
        'datePublished': article.date,
        'inLanguage': 'zh-CN',
        'image': article.imageUrl,
        'url': `https://scum-game-analysis.com/article/${article.slug}`,
        'publisher': {
          '@type': 'Organization',
          'name': '人渣游戏深度解析',
        },
        'breadcrumb': {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://scum-game-analysis.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': categoryMap[article.category] || article.category, 'item': `https://scum-game-analysis.com/category/${article.category}` },
            { '@type': 'ListItem', 'position': 3, 'name': article.title },
          ],
        },
      }) }} />
    </>
  );
}